import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import GenericEditor from './GenericEditor';
import PhotoUpload from './PhotoUpload';
import HistoryEditor from './HistoryEditor';
import { useSiteData } from '@/context/SiteDataContext';

/**
 * WomensAdminEditor
 * -----------------
 * A single tab that gives the Women's Ministry admin full control over its
 * mini-site: header, footer, colors/fonts, hero, navigation tabs, and hooks
 * back into shared editors (branches, gallery-tag advisory) — all while the
 * Women's Ministry remains part of the main site.
 */
export default function WomensAdminEditor() {
  const [tab, setTab] = useState('appearance');
  const TABS = [
    { id: 'appearance', label: 'Header · Colors · Fonts' },
    { id: 'home', label: "Home Page Content" },
    { id: 'nav', label: 'Menu Tabs' },
    { id: 'branches', label: 'Branches' },
    { id: 'history', label: 'History & Story' },
    { id: 'footer', label: 'Footer' },
  ];
  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`womens-admin-tab-${t.id}`}
            className={`text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full transition ${
              tab === t.id ? 'bg-royal text-white' : 'bg-white text-royal border border-royal/30 hover:border-royal'
            }`}>{t.label}</button>
        ))}
      </div>
      {tab === 'appearance' && <AppearanceForm />}
      {tab === 'home' && <HomeContentForm />}
      {tab === 'nav' && (
        <>
          <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
            <strong className="text-royal">Women's Ministry Menu</strong> — add, rename, reorder, or hide any tab.
            The mini-site sub-navigation at <code className="text-royal">/womens</code> reflects this list live.
            Set <code>href</code> to a built-in route (e.g. <code>/womens/branches</code>) or any custom path.
          </div>
          <GenericEditor title="Women's Menu Tabs" endpoint="/site/womens-nav" category="wnav" imageKey={null}
            fields={[
              { key: 'label', label: 'Menu label', type: 'text' },
              { key: 'href', label: 'URL / path', type: 'text' },
              { key: 'visible', label: 'Visible (uncheck to hide without deleting)', type: 'boolean' },
            ]} />
        </>
      )}
      {tab === 'branches' && (
        <GenericEditor title="Women's Branches" endpoint="/site/womens-branches" category="womens" imageKey="image"
          fields={[
            { key: 'name', label: 'Branch Name', type: 'text' },
            { key: 'slug', label: 'Slug / short key', type: 'text' },
            { key: 'tagline', label: 'Tagline / Motto', type: 'text' },
            { key: 'logo', label: 'Branch Logo (square)', type: 'photo', photoCategory: 'womens' },
            { key: 'image', label: 'Cover Image (banner)', type: 'photo', photoCategory: 'womens' },
            { key: 'leader_name', label: 'Branch Leader — Name', type: 'text' },
            { key: 'leader_title', label: 'Branch Leader — Official Title', type: 'text' },
            { key: 'leader_photo', label: 'Branch Leader — Photo', type: 'photo', photoCategory: 'womens' },
            { key: 'description', label: 'Description', type: 'textarea', rows: 6 },
            { key: 'mission', label: 'Mission / Purpose', type: 'textarea', rows: 3 },
            { key: 'email', label: 'Contact Email', type: 'text' },
            { key: 'phone', label: 'Contact Phone', type: 'text' },
            { key: 'address', label: 'Meeting Address (optional)', type: 'text' },
          ]} />
      )}
      {tab === 'footer' && <FooterForm />}
      {tab === 'history' && (
        <>
          <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
            <strong className="text-royal">History &amp; Story</strong> — build the timeline that appears at <code className="text-royal">/womens/history</code>.
            Add entries with a year, date, title, paragraph, and photo. Choose photo side (left/right) — the layout alternates naturally.
            Visitors can click any year on the top timeline strip to jump to that moment. Remember to add the tab in <em>Menu Tabs</em> so it appears in the mini-site nav.
          </div>
          <HistoryEditor />
        </>
      )}
    </div>
  );
}

// ---------------- Sub-forms ----------------

function AppearanceForm() {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { refresh } = useSiteData();
  useEffect(() => { api.get('/site/settings').then(r => setForm(r.data)).catch(() => toast.error('Load failed')); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/site/settings', form);
      toast.success('Women\'s theme saved · live');
      refresh?.(['settings']);
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [homeContent, setHomeContent] = useState({});
  useEffect(() => { api.get('/site/page-content/womens-home').then(r => setHomeContent(r.data || {})).catch(() => {}); }, []);
  const saveHome = async () => {
    setBusy(true);
    try { await api.patch('/site/page-content/womens-home', homeContent); toast.success('Header saved · live'); }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const setH = (k, v) => setHomeContent(p => ({ ...p, [k]: v }));

  return (
    <div className="max-w-4xl space-y-6">
      <div className="bg-white border rounded-lg p-6 space-y-4">
        <h4 className="font-serif text-lg text-royal font-bold">Header / Brand</h4>
        <PhotoUpload label="Women's Ministry logo (round)" value={homeContent.womens_logo} onChange={(v) => setH('womens_logo', v)} category="womens" />
        <FieldRow>
          <FieldLabel>Women's Ministry name (shown in header)</FieldLabel>
          <input value={homeContent.womens_name || ''} onChange={(e) => setH('womens_name', e.target.value)} data-testid="womens-name-input" className="w-full px-3 py-2 border rounded" />
        </FieldRow>
        <FieldRow>
          <FieldLabel>Motto / tagline (small text under name)</FieldLabel>
          <input value={homeContent.motto || ''} onChange={(e) => setH('motto', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </FieldRow>
        <PhotoUpload label="Home page hero background image" value={homeContent.womens_hero_image} onChange={(v) => setH('womens_hero_image', v)} category="womens" />
        <FieldRow>
          <FieldLabel>Home page hero background VIDEO (mp4 URL — overrides image if set)</FieldLabel>
          <input value={homeContent.hero_video_url || ''} onChange={(e) => setH('hero_video_url', e.target.value)} data-testid="womens-hero-video-url" placeholder="https://.../video.mp4" className="w-full px-3 py-2 border rounded font-mono text-xs" />
        </FieldRow>
        <PhotoUpload label="Or upload a new hero video (mp4)" value={homeContent.hero_video_url} onChange={(v) => setH('hero_video_url', v)} category="womens" accept="video/*" />
        <PhotoUpload label="About page hero image" value={homeContent.about_hero_image} onChange={(v) => setH('about_hero_image', v)} category="womens" />
        <FieldRow>
          <FieldLabel>About page hero VIDEO (mp4 URL — overrides image if set)</FieldLabel>
          <input value={homeContent.about_hero_video_url || ''} onChange={(e) => setH('about_hero_video_url', e.target.value)}
            data-testid="womens-about-hero-video-url"
            placeholder="https://.../video.mp4" className="w-full px-3 py-2 border rounded font-mono text-xs" />
        </FieldRow>
        <PhotoUpload label="Or upload About page hero video (mp4)" value={homeContent.about_hero_video_url} onChange={(v) => setH('about_hero_video_url', v)} category="womens" accept="video/*" />
        <PhotoUpload label="Supervisor portrait (Home welcome section — overrides leader profile photo)" value={homeContent.supervisor_photo} onChange={(v) => setH('supervisor_photo', v)} category="womens" />
        <FieldRow>
          <FieldLabel>Hero intro paragraph</FieldLabel>
          <textarea rows="3" value={homeContent.hero_intro || ''} onChange={(e) => setH('hero_intro', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </FieldRow>
        <FieldRow>
          <FieldLabel>Supervisor welcome message</FieldLabel>
          <textarea rows="5" value={homeContent.supervisor_welcome || ''} onChange={(e) => setH('supervisor_welcome', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </FieldRow>
        <div><button onClick={saveHome} disabled={busy} data-testid="womens-header-save" className="btn-royal">{busy ? 'Saving…' : 'Save Header'}</button></div>
      </div>
      <div className="bg-white border rounded-lg p-6 space-y-4">
        <h4 className="font-serif text-lg text-royal font-bold">Colors & Fonts (mini-site scoped)</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <FieldRow>
            <FieldLabel>Primary color (headers, buttons)</FieldLabel>
            <div className="flex gap-2">
              <input type="color" value={form.womens_primary_color || '#5A2882'} onChange={(e) => set('womens_primary_color', e.target.value)} className="w-12 h-10 rounded border" data-testid="womens-primary-picker" />
              <input value={form.womens_primary_color || ''} onChange={(e) => set('womens_primary_color', e.target.value)} className="flex-1 px-3 py-2 border rounded font-mono text-xs" />
            </div>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Accent color (highlights)</FieldLabel>
            <div className="flex gap-2">
              <input type="color" value={form.womens_accent_color || '#E4C169'} onChange={(e) => set('womens_accent_color', e.target.value)} className="w-12 h-10 rounded border" data-testid="womens-accent-picker" />
              <input value={form.womens_accent_color || ''} onChange={(e) => set('womens_accent_color', e.target.value)} className="flex-1 px-3 py-2 border rounded font-mono text-xs" />
            </div>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Heading font</FieldLabel>
            <select value={form.womens_heading_font || 'Playfair Display'} onChange={(e) => set('womens_heading_font', e.target.value)} className="w-full px-3 py-2 border rounded">
              {['Playfair Display', 'Cinzel', 'Cormorant Garamond', 'Libre Baskerville', 'DM Serif Display', 'Lora'].map(f => <option key={f}>{f}</option>)}
            </select>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Body font</FieldLabel>
            <select value={form.womens_body_font || 'Poppins'} onChange={(e) => set('womens_body_font', e.target.value)} className="w-full px-3 py-2 border rounded">
              {['Poppins', 'Inter', 'Roboto', 'Open Sans', 'Nunito Sans', 'DM Sans', 'Lato'].map(f => <option key={f}>{f}</option>)}
            </select>
          </FieldRow>
        </div>
        <div><button onClick={save} disabled={busy} data-testid="womens-theme-save" className="btn-royal">{busy ? 'Saving…' : 'Save Theme'}</button></div>
      </div>
    </div>
  );
}

function HomeContentForm() {
  const [c, setC] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/site/page-content/womens-home').then(r => setC(r.data || {})).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.patch('/site/page-content/womens-home', c); toast.success('Saved · live'); }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const set = (k, v) => setC(p => ({ ...p, [k]: v }));
  const F = ({ k, label, rows = 3 }) => (
    <FieldRow>
      <FieldLabel>{label}</FieldLabel>
      <textarea rows={rows} value={c[k] || ''} onChange={(e) => set(k, e.target.value)} className="w-full px-3 py-2 border rounded" />
    </FieldRow>
  );
  return (
    <div className="max-w-4xl bg-white border rounded-lg p-6 space-y-4">
      <h4 className="font-serif text-lg text-royal font-bold">Women's Home Page Content</h4>
      <F k="hero_overline" label="Hero overline (small text)" rows={1} />
      <F k="hero_intro" label="Hero intro paragraph" />
      <F k="supervisor_welcome" label="Supervisor welcome message" rows={5} />
      <F k="about_blurb" label="About blurb (About page hero)" />
      <F k="about_body" label="About body (long)" rows={6} />
      <F k="about_mission" label="About page — Mission" rows={2} />
      <F k="about_vision" label="About page — Vision" rows={2} />
      <div><button onClick={save} disabled={busy} data-testid="womens-home-save" className="btn-royal">{busy ? 'Saving…' : 'Save'}</button></div>
    </div>
  );
}

function FooterForm() {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { refresh } = useSiteData();
  useEffect(() => { api.get('/site/settings').then(r => setForm(r.data)).catch(() => toast.error('Load failed')); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.patch('/site/settings', form); toast.success('Saved · live'); refresh?.(['settings']); }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="max-w-4xl bg-white border rounded-lg p-6 space-y-4">
      <h4 className="font-serif text-lg text-royal font-bold">Women's Ministry Contact / Footer</h4>
      <FieldRow>
        <FieldLabel>Footer tagline</FieldLabel>
        <textarea rows="2" value={form.womens_footer_tagline || ''} onChange={(e) => set('womens_footer_tagline', e.target.value)} className="w-full px-3 py-2 border rounded" />
      </FieldRow>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldRow>
          <FieldLabel>Contact email</FieldLabel>
          <input value={form.womens_email || ''} onChange={(e) => set('womens_email', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </FieldRow>
        <FieldRow>
          <FieldLabel>Contact phone</FieldLabel>
          <input value={form.womens_phone || ''} onChange={(e) => set('womens_phone', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </FieldRow>
      </div>
      <div><button onClick={save} disabled={busy} data-testid="womens-footer-save" className="btn-royal">{busy ? 'Saving…' : 'Save'}</button></div>
    </div>
  );
}

function FieldRow({ children }) { return <div className="space-y-1">{children}</div>; }
function FieldLabel({ children }) { return <div className="text-sm font-semibold text-royal">{children}</div>; }
